import React from 'react';
import { FileX, Inbox, Search, ShoppingBag, Users, Star, MessageSquare } from 'lucide-react';
import './EmptyState.css';

/**
 * EmptyState Component
 *
 * @param {string} type - Predefined type: 'default' | 'orders' | 'customers' | 'reviews' | 'search' | 'messages'
 * @param {ReactNode} icon - Custom icon
 * @param {string} title - Main title
 * @param {string} description - Description text
 * @param {ReactNode} action - Action button/element
 */
const EmptyState = ({
  type = 'default',
  icon,
  title,
  description,
  action,
  className = '',
}) => {
  // Predefined configurations
  const presets = {
    default: {
      icon: <Inbox size={48} />,
      title: 'No data available',
      description: 'There is no data to display at the moment.',
    },
    orders: {
      icon: <ShoppingBag size={48} />,
      title: 'No orders yet',
      description: 'When customers place orders, they will appear here.',
    },
    customers: {
      icon: <Users size={48} />,
      title: 'No customers yet',
      description: 'Your customer list will grow as people place orders.',
    },
    reviews: {
      icon: <Star size={48} />,
      title: 'No reviews yet',
      description: 'Customer reviews will show up here once they start coming in.',
    },
    search: {
      icon: <Search size={48} />,
      title: 'No results found',
      description: 'Try adjusting your search or filters to find what you\'re looking for.',
    },
    messages: {
      icon: <MessageSquare size={48} />,
      title: 'No messages',
      description: 'Your conversations with customers will appear here.',
    },
    complaints: {
      icon: <FileX size={48} />,
      title: 'No complaints reported',
      description: 'Keep it up! No customer complaints have been filed.',
    },
  };

  const config = presets[type] || presets.default;

  const classNames = [
    'oz-empty-state',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      <div className="oz-empty-state__icon">
        {icon || config.icon}
      </div>
      <h3 className="oz-empty-state__title">
        {title || config.title}
      </h3>
      <p className="oz-empty-state__description">
        {description || config.description}
      </p>
      {action && (
        <div className="oz-empty-state__action">
          {action}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
